/**
 * Error model of the api-client — three disjoint failure kinds:
 *
 *   ApiError           the server answered an error ENVELOPE; the only
 *                      kind carrying a machine `code`. Frontends branch
 *                      on `error.code` (registered in api-contract),
 *                      never on `message` text (contract guidelines §2).
 *   ApiTransportError  the fetch itself failed (offline/DNS/CORS) or the
 *                      per-attempt timeout fired (ApiTimeoutError).
 *                      Retryable per the retry policy.
 *   ApiResponseError   the HTTP exchange completed but the body violated
 *                      the envelope contract (non-JSON, malformed, empty).
 *                      Indicates a proxy/backend bug, not user error.
 *
 * Every kind carries `requestId` when one is known (sent id, echoed by
 * the server) so user-visible error reports can quote it for support.
 */
import { ERROR_CODES } from '@web-v4/api-contract';
const REGISTERED_CODES = new Set(ERROR_CODES);
/** Narrows a code to the registered registry when known. */
export function isRegisteredErrorCode(code) {
    return REGISTERED_CODES.has(code);
}
/** The server answered an error envelope ({error:{code,message,fields?}}). */
export class ApiError extends Error {
    name = 'ApiError';
    /** Stable machine code — the ONLY branch key. */
    code;
    /** HTTP status the envelope arrived with (contractual per code). */
    status;
    /** Server-echoed request id (envelope request_id). */
    requestId;
    /** Per-field messages for validation failures; undefined otherwise. */
    fields;
    /** false when the backend emitted a code this build doesn't know. */
    isRegisteredCode;
    constructor(init) {
        super(init.message);
        this.code = init.code;
        this.status = init.status;
        this.requestId = init.requestId;
        this.fields = init.fields;
        this.isRegisteredCode = isRegisteredErrorCode(init.code);
    }
    /** Type-safe branch helper: `if (err.is('csrf_failed')) ...` narrows nothing but reads clearly. */
    is(code) {
        return this.code === code;
    }
}
/** fetch rejected before a response arrived (network/CORS/aborted externally). */
export class ApiTransportError extends Error {
    name = 'ApiTransportError';
    requestId;
    constructor(init) {
        super(init.message, { cause: init.cause });
        this.requestId = init.requestId;
    }
}
/** The per-attempt timeout fired (the attempt's AbortController aborted it). */
export class ApiTimeoutError extends ApiTransportError {
    name = 'ApiTimeoutError';
    /** The timeout that fired, in milliseconds. */
    timeoutMs;
    constructor(init) {
        super(init);
        this.timeoutMs = init.timeoutMs;
    }
}
/** The response broke the envelope contract (non-JSON / malformed / empty). */
export class ApiResponseError extends Error {
    name = 'ApiResponseError';
    status;
    requestId;
    bodySnippet;
    constructor(init) {
        super(init.message);
        this.status = init.status;
        this.requestId = init.requestId;
        this.bodySnippet = init.bodySnippet;
    }
}
//# sourceMappingURL=errors.js.map